import React from "react";
import { connect } from "react-redux";
import { toastr } from "react-redux-toastr";
import T from "prop-types";
import { Button, Col } from "react-bootstrap";
import { reduxForm, Field, FieldArray } from "redux-form";
import { Body, Footer } from "MODULES/shared/components/Modal";
import {
  Check,
  CheckGroup,
  FormInput,
  FormRow
} from "MODULES/shared/components/Forms";
import { properCase } from "LIB/formatters/text";
import CurrencyField from "MODULES/shared/components/Forms/CurrencyField/CurrencyField";
import {
  BROKERED_EXCHANGE,
  CORE_REPAIR_INVOICE_AMOUNT_PAID,
  CORE_REPAIR_INVOICE_PAID,
  FLAT_EXCHANGE,
  SALES_ORDER
} from "MODULES/SalesOrders/constants";
import { hasRole } from "MODULES/users/helpers";
import { ACCOUNTING } from "MODULES/users/constants";
import { useCurrentUser } from "APP_ROOT/hooks";
import moment from "moment";
import { INVOICE_DATE, INVOICE_DUE_DATE } from "MODULES/Lots/constants";
import { isEmpty } from "lodash";
import FirstExchangeWarning from "MODULES/SalesOrders/components/Detail/Panel/Billing/FirstExchangeWarning";
import { withFieldAccessors } from "MODULES/shared/decorators/withFieldAccessors";
import {
  AMOUNT_PAID,
  INVOICE_NUMBER,
  PURCHASE_ORDER
} from "../../../PurchaseOrders/constants";

const PAID_IN_FULL = "paid_in_full";

class Form extends React.Component {
  setToFullVal = () => {
    const { order, includeCancelled = false } = this.props;
    const lineItems = includeCancelled
      ? order.line_items
      : order.line_items.filter(l => !l.cancelled);

    lineItems.forEach((l, idx) => {
      this.props.change(
        `line_items[${idx}].${AMOUNT_PAID}`,
        Number(this.fieldAmount(l))
      );
      this.props.change(`line_items[${idx}].${PAID_IN_FULL}`, true);
      this.setInvoicePaidDate(idx);
    });
  };

  setItemToFullVal = idx => {
    const { order, includeCancelled = false } = this.props;
    const lineItems = includeCancelled
      ? order.line_items
      : order.line_items.filter(l => !l.cancelled);

    const lineItem = lineItems[idx];
    this.props.change(
      `line_items[${idx}].${AMOUNT_PAID}`,
      Number(this.fieldAmount(lineItem))
    );
  };

  setInvoicePaidDate = idx => {
    const date = moment().format("YYYY-MM-DD");
    if (!this.props.getFieldValue(`line_items[${idx}].invoice_paid_date`)) {
      this.props.change(`line_items[${idx}].invoice_paid_date`, date);
    }
  };

  handleAmountFormChange = (ev, field) => {
    const val = ev.target ? ev.target.value : null;
    const date = moment().format("YYYY-MM-DD");
    if (val) {
      this.props.change(`line_items[${0}].${field}`, date);
    }
  };

  handleTopLevelFormChange = (ev, field) => {
    const { order, includeCancelled = false } = this.props;
    const val = ev.target.value;

    const lineItems = includeCancelled
      ? order.line_items
      : order.line_items.filter(l => !l.cancelled);

    lineItems.forEach((l, idx) => {
      this.props.change(`line_items[${idx}].${field}`, val);
    });
  };

  fieldAmount = lineItem => {
    const { orderType } = this.props;

    if (orderType === PURCHASE_ORDER) {
      return lineItem.cost * lineItem.quantity;
    }

    return lineItem.exchange_sale
      ? lineItem.exchange_fee
      : lineItem.quantity * lineItem.price_per_unit;
  };

  renderLineItems = ({ fields, orderType, lineItems }) => (
    <tbody>
      {fields.map((item, idx) => {
        const lineItem = lineItems[idx];
        if (!lineItem) return null;

        const currentUser = useCurrentUser();
        const shouldEnterAmount =
          lineItem[BROKERED_EXCHANGE] || lineItem[FLAT_EXCHANGE];
        const disabled = !hasRole(currentUser, ACCOUNTING);

        return (
          <tr>
            <td>
              <FormRow>
                <Col md={3}>
                  <Field
                    component={FormInput}
                    name={`${item}.part.part_number`}
                    label="Part Number"
                    disabled
                  />
                </Col>
                {this.props.displaySerial && (
                  <Col md={2}>
                    <Field
                      component={FormInput}
                      name={`${item}.inventory.serial_number`}
                      label="Serial Number"
                      disabled
                    />
                  </Col>
                )}
                <Col md={3}>
                  <Field
                    component={FormInput}
                    name={`${item}.invoice_number`}
                    label="Invoice Number"
                  />
                </Col>
                {orderType === PURCHASE_ORDER && (
                  <Col md={3}>
                    <Field
                      component={FormInput}
                      name={`${item}.invoice_date`}
                      label="Invoice Date"
                      type="date"
                    />
                  </Col>
                )}
              </FormRow>
              <FormRow>
                <Col md={3}>
                  <Field
                    component={FormInput}
                    name={`${item}.invoice_due_date`}
                    label="Invoice Due Date"
                    type="date"
                  />
                </Col>
                <Col md={3}>
                  <Field
                    component={FormInput}
                    name={`${item}.invoice_paid_date`}
                    label="Invoice Paid Date"
                    type="date"
                  />
                </Col>
                <Col md={3}>
                  <Field
                    component={CurrencyField}
                    name={[item, AMOUNT_PAID].join(".")}
                    label={properCase(AMOUNT_PAID)}
                  />
                </Col>
                <Col md={2}>
                  <CheckGroup justified title="Amt Paid In Full">
                    <Field
                      name={`${item}.${PAID_IN_FULL}`}
                      component={Check}
                      label="Yes"
                      onChange={() => {
                        this.setItemToFullVal(idx);
                        this.setInvoicePaidDate(idx);
                      }}
                    />
                  </CheckGroup>
                </Col>
              </FormRow>

              {lineItem.exchange_sale && (
                <React.Fragment>
                  <FirstExchangeWarning lineItem={lineItem} />
                  {(isEmpty(lineItem.exchange_type) ||
                    !isEmpty(lineItem.core_repair_invoice_number)) && (
                    <FormRow>
                      <hr className="hr-sm" style={{ marginTop: 0 }} />
                      <Col md={2}>
                        <Field
                          name={`${item}.core_repair_invoice_number`}
                          component={FormInput}
                          disabled={disabled}
                          label="Core Repair Inv. #"
                        />
                      </Col>
                      <Col md={2}>
                        <Field
                          name={`${item}.${CORE_REPAIR_INVOICE_AMOUNT_PAID}`}
                          component={CurrencyField}
                          disabled={disabled || !shouldEnterAmount}
                          label="Amount"
                          onChange={e =>
                            this.handleAmountFormChange(
                              e,
                              CORE_REPAIR_INVOICE_PAID
                            )}
                        />
                      </Col>
                      <Col md={2}>
                        <Field
                          name={`${item}.${CORE_REPAIR_INVOICE_PAID}`}
                          component={FormInput}
                          disabled={disabled}
                          type="date"
                          label="Core Repair Inv Paid Date"
                        />
                      </Col>
                    </FormRow>
                  )}
                </React.Fragment>
              )}
            </td>
          </tr>
        );
      })}
    </tbody>
  );

  render() {
    const {
      order,
      orderType,
      handleSubmit,
      close,
      invalid,
      submitting,
      includeCancelled
    } = this.props;

    const relevantLineItems = includeCancelled
      ? order.line_items
      : order.line_items.filter(
          l =>
            orderType === SALES_ORDER ? !l.cancelled || l.shipped : !l.cancelled
        );

    const shouldRenderSection = relevantLineItems.length > 1;

    return (
      <form onSubmit={handleSubmit}>
        <Body>
          {shouldRenderSection && (
            <FormRow>
              <Col md={3}>
                <Field
                  component={FormInput}
                  name="Invoice #"
                  label={properCase(INVOICE_NUMBER)}
                  onChange={e =>
                    this.handleTopLevelFormChange(e, INVOICE_NUMBER)}
                />
              </Col>
              {PURCHASE_ORDER === orderType && (
                <Col md={2}>
                  <Field
                    component={FormInput}
                    name={INVOICE_DATE}
                    label={properCase(INVOICE_DATE)}
                    type="date"
                    onChange={e =>
                      this.handleTopLevelFormChange(e, INVOICE_DATE)}
                  />
                </Col>
              )}
              {[PURCHASE_ORDER, SALES_ORDER].includes(orderType) && (
                <React.Fragment>
                  <Col md={3}>
                    <Field
                      component={FormInput}
                      name={INVOICE_DUE_DATE}
                      label={properCase(INVOICE_DUE_DATE)}
                      type="date"
                      onChange={e =>
                        this.handleTopLevelFormChange(e, INVOICE_DUE_DATE)}
                    />
                  </Col>
                  <Col md={2}>
                    <Field
                      component={FormInput}
                      name="invoice_paid_date"
                      label="Invoice Paid Date"
                      type="date"
                      onChange={e =>
                        this.handleTopLevelFormChange(e, "invoice_paid_date")}
                    />
                  </Col>
                </React.Fragment>
              )}
              <Col md={2}>
                <CheckGroup justified title="Amt Paid In Full">
                  <Field
                    name={PAID_IN_FULL}
                    component={Check}
                    label="Yes"
                    onChange={this.setToFullVal}
                  />
                </CheckGroup>
              </Col>
            </FormRow>
          )}

          <table className="table table-striped table-numbered margin-bottom-none">
            <FieldArray
              name="line_items"
              component={this.renderLineItems}
              // Pass in the full array (or filtered if !includeCancelled)
              lineItems={relevantLineItems}
              orderType={orderType}
            />
          </table>
        </Body>
        <Footer>
          <Button onClick={close}>Cancel</Button>
          <Button
            type="submit"
            bsStyle="primary"
            disabled={invalid || submitting}
          >
            &nbsp;Update Invoice Details
          </Button>
        </Footer>
      </form>
    );
  }
}

Form.propTypes = {
  change: T.func.isRequired,
  close: T.func.isRequired,
  displaySerial: T.bool,
  handleSubmit: T.func.isRequired,
  includeCancelled: T.bool,
  invalid: T.bool.isRequired,
  order: T.shape({}),
  orderType: T.string,
  submitting: T.bool.isRequired,
  getFieldValue: T.func
};

export default connect(undefined, (dispatch, ownProps) => ({
  onSubmit: data => {
    dispatch(ownProps.bulkUpdate(data))
      .then(() => {
        ownProps.close();
      })
      .catch(error => {
        toastr.error("Error", error.message);
      });
  }
}))(
  reduxForm({
    form: "editInvoiceDetails"
  })(withFieldAccessors(Form))
);



// WEBPACK FOOTER //
// ./app/modules/Orders/shared/EditInvoiceDetailsForm/EditInvoiceDetailsForm.jsx